import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layers, RefreshCw, MoreVertical, Pencil, FileDown, ArrowRightLeft,
  CheckCircle2, XCircle, Users, MapPin, CalendarDays, UserCircle,
} from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DropdownMenu, type DropdownItem } from '@/components/ui/DropdownMenu'
import { ProposalsService, type KanbanColumn, type KanbanCard } from '@/services/modules/ProposalsService'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'

export function ProposalKanbanPage() {
  const nav = useNavigate()
  const [cols, setCols] = useState<KanbanColumn[]>([])
  const [loading, setLoading] = useState(false)
  const [dragId, setDragId] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)
  const [userFilter, setUserFilter] = useState('')
  const [canFilterByUser, setCanFilterByUser] = useState(false)
  const [users, setUsers] = useState<{ id: string; name: string }[]>([])
  const [winning, setWinning] = useState<KanbanCard | null>(null)
  const [losing, setLosing] = useState<KanbanCard | null>(null)
  const [closing, setClosing] = useState(false)

  const load = useCallback(async (uid: string) => {
    setLoading(true)
    try {
      const { columns, canFilterByUser } = await ProposalsService.kanban(uid)
      setCols(columns)
      setCanFilterByUser(canFilterByUser)
    }
    catch { toast.error('Erro ao carregar kanban.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load(userFilter) }, [load, userFilter])

  useEffect(() => {
    if (!canFilterByUser) return
    ProposalsService.kanbanUsers().then(setUsers).catch(() => setUsers([]))
  }, [canFilterByUser])

  async function moveTo(cardId: string, targetStageId: string) {
    setCols(prev => {
      const next = prev.map(c => ({ ...c, proposals: c.proposals.filter(p => p.id !== cardId) }))
      const card = prev.flatMap(c => c.proposals).find(p => p.id === cardId)
      if (card) {
        const target = next.find(c => c.id === targetStageId)
        if (target) target.proposals = [...target.proposals, card]
      }
      return next
    })
    setDragOver(null); setDragId(null)
    try { await ProposalsService.updateStage(cardId, targetStageId); toast.success('Etapa atualizada.') }
    catch { toast.error('Erro ao mover. Recarregando'); load(userFilter) }
  }

  function drop(targetStageId: string) {
    if (!dragId) return
    moveTo(dragId, targetStageId)
  }

  async function confirmWin() {
    if (!winning) return
    setClosing(true)
    try {
      await ProposalsService.win(winning.id)
      toast.success({ title: 'Proposta ganha', description: `${winning.companyName} saiu do kanban.` })
      setWinning(null); load(userFilter)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao marcar como ganha.') }
    finally { setClosing(false) }
  }
  async function confirmLose() {
    if (!losing) return
    setClosing(true)
    try {
      await ProposalsService.lose(losing.id)
      toast.success({ title: 'Proposta perdida', description: `${losing.companyName} saiu do kanban.` })
      setLosing(null); load(userFilter)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao marcar como perdida.') }
    finally { setClosing(false) }
  }

  function menuItems(card: KanbanCard, currentStageId: string): DropdownItem[] {
    const items: DropdownItem[] = [
      { key: 'edit', label: 'Ver e editar', icon: <Pencil size={14} />, onClick: () => nav(`/commercial/proposals/${card.id}/editar`) },
      { key: 'pdf', label: card.isGenerated ? 'Baixar PDF' : 'Gerar PDF', icon: <FileDown size={14} />, onClick: () => nav(`/commercial/proposals/${card.id}/editar`) },
    ]
    cols.forEach((c, i) => items.push({
      key: `stage-${c.id}`, label: c.stage, icon: <ArrowRightLeft size={14} />,
      divider: i === 0, disabled: c.id === currentStageId, onClick: () => moveTo(card.id, c.id),
    }))
    items.push({ key: 'win', label: 'Marcar como ganha', icon: <CheckCircle2 size={14} />, divider: true, onClick: () => setWinning(card) })
    items.push({ key: 'lose', label: 'Marcar como perdida', icon: <XCircle size={14} />, danger: true, onClick: () => setLosing(card) })
    return items
  }

  return (
    <>
      <PageHeader title="Kanban de propostas" subtitle="Arraste os cartões entre as etapas"
        crumbs={[{ label: 'Comercial' }, { label: 'Propostas', to: '/commercial/proposals' }, { label: 'Kanban' }]}
        actions={
          <div className="flex items-center gap-2">
            {canFilterByUser && (
              <Select
                wrapperClassName="w-56"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                options={users.map(u => ({ value: u.id, label: u.name }))}
                placeholder="Todos os colaboradores"
                searchPlaceholder="Buscar colaborador"
              />
            )}
            <Button variant="secondary" iconLeft={<RefreshCw size={14} />} onClick={() => load(userFilter)} loading={loading}>Recarregar</Button>
          </div>
        }
      />

      <div className="flex gap-3 overflow-x-auto pb-3">
        {cols.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center w-full py-14 text-gray-400">
            <Layers size={32} className="mb-2 opacity-40" />
            <p className="text-sm">Crie etapas em <strong>Configurações → Comercial → Etapas</strong> para começar.</p>
          </div>
        )}
        {cols.map(col => (
          <div
            key={col.id}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.id) }}
            onDragLeave={() => setDragOver(prev => prev === col.id ? null : prev)}
            onDrop={() => drop(col.id)}
            className={cn(
              'w-80 shrink-0 bg-gray-50 rounded-md ring-1 ring-gray-200 p-3 flex flex-col',
              dragOver === col.id && 'ring-brand-400 bg-brand-50/40',
            )}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">{col.stage}</h3>
              <Badge variant="gray">{col.proposals.length}</Badge>
            </div>
            <div className="flex-1 space-y-2 min-h-[80px]">
              {col.proposals.map(p => (
                <ProposalCardView
                  key={p.id}
                  card={p}
                  menuItems={menuItems(p, col.id)}
                  onDragStart={() => setDragId(p.id)}
                  onDragEnd={() => { setDragId(null); setDragOver(null) }}
                />
              ))}
              {col.proposals.length === 0 && (
                <div className="text-[11px] text-gray-400 text-center py-6 border-2 border-dashed border-gray-200 rounded-md">
                  Solte um cartão aqui
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!winning} onClose={() => setWinning(null)} onConfirm={confirmWin} loading={closing}
        title="Marcar como ganha?"
        description={`Ao marcar como ganha, "${winning?.companyName ?? ''}" sai do kanban. Você pode reabri-la depois na listagem.`}
        confirmLabel="Marcar como ganha"
      />
      <ConfirmDialog
        open={!!losing} onClose={() => setLosing(null)} onConfirm={confirmLose} loading={closing}
        title="Marcar como perdida?" variant="danger"
        description={`Ao marcar como perdida, "${losing?.companyName ?? ''}" sai do kanban. Você pode reabri-la depois na listagem.`}
        confirmLabel="Marcar como perdida"
      />
    </>
  )
}

// =============================================================================
// Card
// =============================================================================
function ProposalCardView({ card, menuItems, onDragStart, onDragEnd }: {
  card: KanbanCard
  menuItems: DropdownItem[]
  onDragStart: () => void
  onDragEnd: () => void
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="bg-white rounded-md ring-1 ring-gray-200 p-3 cursor-grab active:cursor-grabbing hover:ring-brand-300 transition"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-800 break-words">{card.companyName}</div>
          {card.recipientName && <div className="text-[11px] text-gray-500 mt-0.5">A/C: {card.recipientName}</div>}
        </div>
        <DropdownMenu items={menuItems} trigger={<MoreVertical size={16} />} ariaLabel="Ações da proposta" />
      </div>

      <div className="mt-2 flex items-center gap-2">
        <StatusBadge status={card.status} />
        <span className="inline-flex items-center gap-1 text-[11px] text-gray-600"><Users size={11} className="text-gray-400" /> {card.livesCount} vidas</span>
      </div>

      {card.city && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500">
          <MapPin size={11} className="text-gray-400 shrink-0" /> <span className="break-words">{card.city}</span>
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <CalendarDays size={10} /> {card.formatted?.createdAt ?? '—'}
        </div>
        {card.createdByName && (
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
            <UserCircle size={10} /> {card.createdByName}
          </div>
        )}
      </div>
    </div>
  )
}
